import { useContext, useMemo } from 'react';

import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

import { SECONDS_IN_DAY } from '@cast/constants';
import { useCluster } from '@cast/shared-cluster-context-data-access';
import { WorkloadDetailsContext } from '@cast/split-me/features/cluster/workload-optimization';
import { useWorkloadCostReportQuery } from '@cast/split-me/hooks';
import { attachTimeTicks } from '@cast/split-me/utils';
import { toNumOrZero } from '@cast/utils';
import { WorkloadCostMetric } from '@cast/types';

import { useInventoryWorkloadsTimeRangeSelection } from '../../../../_hooks/useInventoryWorkloadsTimeRangeSelection';

dayjs.extend(utc);
dayjs.extend(timezone);

export type WorkloadComputeSpendChartPoint = {
  timestamp: string;
  chartTimestampUnix: number;
  onDemand: number;
  spot: number;
  fallback: number;
  storage: number;
  forecast: boolean;
};

export type WorkloadComputeSpendTotals = {
  onDemand: number;
  spot: number;
  fallback: number;
  storage: number;
  total: number;
};

const MINUTES_IN_HOUR = 60;
const FORECAST_WINDOW_DAYS = 7;

type AggregatedPoint = {
  timestamp: string;
  onDemandCost: number;
  spotCost: number;
  fallbackCost: number;
  storageCost: number;
  onDemandUptime: number;
  spotUptime: number;
  fallbackUptime: number;
  forecast: boolean;
};

const aggregateMetrics = (
  metrics: WorkloadCostMetric[] | undefined
): Record<string, AggregatedPoint> => {
  const byDate: Record<string, AggregatedPoint> = {};

  if (!metrics) {
    return byDate;
  }

  for (const metric of metrics) {
    if (!metric.timestamp) {
      continue;
    }

    const timestamp = dayjs.utc(metric.timestamp).toISOString();
    const dateKey = dayjs.utc(timestamp).format('YYYY-MM-DD');

    const onDemandCost = toNumOrZero(metric.costOnDemand);
    const spotCost = toNumOrZero(metric.costSpot);
    const fallbackCost = toNumOrZero(metric.costSpotFallback);
    // The API returns daily storage cost for workload-scoped reports.
    const storageCost = toNumOrZero(metric.storageCost);

    const onDemandUptime = toNumOrZero(metric.uptimeMinutesOnDemand);
    const spotUptime = toNumOrZero(metric.uptimeMinutesSpot);
    const fallbackUptime = toNumOrZero(metric.uptimeMinutesSpotFallback);

    if (dateKey in byDate) {
      byDate[dateKey].onDemandCost += onDemandCost;
      byDate[dateKey].spotCost += spotCost;
      byDate[dateKey].fallbackCost += fallbackCost;
      byDate[dateKey].storageCost += storageCost;
      byDate[dateKey].onDemandUptime += onDemandUptime;
      byDate[dateKey].spotUptime += spotUptime;
      byDate[dateKey].fallbackUptime += fallbackUptime;
    } else {
      byDate[dateKey] = {
        timestamp,
        onDemandCost,
        spotCost,
        fallbackCost,
        storageCost,
        onDemandUptime,
        spotUptime,
        fallbackUptime,
        forecast: false,
      };
    }
  }

  return byDate;
};

const mean = (values: number[]): number => {
  if (values.length === 0) {
    return 0;
  }

  return values.reduce((sum, value) => sum + value, 0) / values.length;
};

const toDailyCost = (cost: number, uptimeMinutes: number): number =>
  (cost * uptimeMinutes) / MINUTES_IN_HOUR;

const generateForecast = (
  actualByDate: Record<string, AggregatedPoint>,
  range: { from: string; to: string },
  timezone: string
): Record<string, AggregatedPoint> => {
  const actualDates = Object.keys(actualByDate).sort();

  if (actualDates.length === 0) {
    return actualByDate;
  }

  const firstFutureDate = dayjs().tz(timezone).startOf('day').add(1, 'day');
  const lastDate = dayjs.utc(range.to);

  if (!lastDate.isAfter(firstFutureDate)) {
    return actualByDate;
  }

  const result: Record<string, AggregatedPoint> = { ...actualByDate };

  let current = firstFutureDate.clone();

  while (!current.isAfter(lastDate)) {
    const dateKey = current.format('YYYY-MM-DD');
    const window = actualDates.slice(-FORECAST_WINDOW_DAYS);

    const onDemandCost = mean(window.map((date) => result[date].onDemandCost));
    const onDemandUptime = mean(
      window.map((date) => result[date].onDemandUptime)
    );
    const meanFallbackCost = mean(
      window.map((date) => result[date].fallbackCost)
    );
    const meanFallbackUptime = mean(
      window.map((date) => result[date].fallbackUptime)
    );

    // Cost-report moves half of fallback into spot forecast and zeros out
    // fallback so the same cost is not counted twice.
    const spotCost =
      mean(window.map((date) => result[date].spotCost)) + meanFallbackCost / 2;
    const spotUptime =
      mean(window.map((date) => result[date].spotUptime)) +
      meanFallbackUptime / 2;
    const fallbackCost = 0;
    const fallbackUptime = 0;

    const storageCost = mean(window.map((date) => result[date].storageCost));

    result[dateKey] = {
      timestamp: current.toISOString(),
      onDemandCost,
      spotCost,
      fallbackCost,
      storageCost,
      onDemandUptime,
      spotUptime,
      fallbackUptime,
      forecast: true,
    };

    actualDates.push(dateKey);
    current = current.add(1, 'day');
  }

  return result;
};

const buildDailyData = (
  aggregatedByDate: Record<string, AggregatedPoint>,
  range: { from: string; to: string },
  timezone: string
): WorkloadComputeSpendChartPoint[] => {
  const withForecast = generateForecast(aggregatedByDate, range, timezone);

  const from = dayjs.utc(range.from);
  const to = dayjs.utc(range.to);
  const points: WorkloadComputeSpendChartPoint[] = [];

  let current = from.clone();

  while (!current.isAfter(to)) {
    const timestamp = current.toISOString();
    const dateKey = current.format('YYYY-MM-DD');
    const aggregated = withForecast[dateKey];

    if (aggregated) {
      points.push({
        timestamp,
        chartTimestampUnix: 0,
        onDemand: toDailyCost(
          aggregated.onDemandCost,
          aggregated.onDemandUptime
        ),
        spot: toDailyCost(aggregated.spotCost, aggregated.spotUptime),
        fallback: toDailyCost(
          aggregated.fallbackCost,
          aggregated.fallbackUptime
        ),
        storage: aggregated.storageCost,
        forecast: aggregated.forecast,
      });
    } else {
      points.push({
        timestamp,
        chartTimestampUnix: 0,
        onDemand: 0,
        spot: 0,
        fallback: 0,
        storage: 0,
        forecast: false,
      });
    }

    current = current.add(1, 'day');
  }

  return attachTimeTicks(points);
};

const buildCumulativeData = (
  dailyData: WorkloadComputeSpendChartPoint[]
): WorkloadComputeSpendChartPoint[] => {
  let onDemandSum = 0;
  let spotSum = 0;
  let fallbackSum = 0;
  let storageSum = 0;

  return dailyData.map((point) => {
    onDemandSum += point.onDemand;
    spotSum += point.spot;
    fallbackSum += point.fallback;
    storageSum += point.storage;

    return {
      ...point,
      onDemand: onDemandSum,
      spot: spotSum,
      fallback: fallbackSum,
      storage: storageSum,
    };
  });
};

const computeTotals = (
  dailyData: WorkloadComputeSpendChartPoint[]
): WorkloadComputeSpendTotals => {
  const totals = dailyData.reduce(
    (acc, point) => ({
      onDemand: acc.onDemand + point.onDemand,
      spot: acc.spot + point.spot,
      fallback: acc.fallback + point.fallback,
      storage: acc.storage + point.storage,
      total: 0,
    }),
    { onDemand: 0, spot: 0, fallback: 0, storage: 0, total: 0 }
  );

  totals.total =
    totals.onDemand + totals.spot + totals.fallback + totals.storage;

  return totals;
};

export const useWorkloadComputeSpendChartData = () => {
  const { workload } = useContext(WorkloadDetailsContext);
  const { cluster } = useCluster();
  const { getRange, timeZone, key } = useInventoryWorkloadsTimeRangeSelection();

  const range = useMemo(() => getRange(), [key]);

  const isRangeSupported = useMemo(
    () => dayjs(range.to).diff(dayjs(range.from), 'day', true) >= 2,
    [range]
  );

  const stepSeconds = SECONDS_IN_DAY;

  const {
    workload: report,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useWorkloadCostReportQuery({
    clusterId: cluster.id,
    namespace: workload?.namespace ?? '',
    workloadType: workload?.kind ?? '',
    workloadName: workload?.name ?? '',
    range: [dayjs(range.from), dayjs(range.to)],
    useListingPrices: false,
    stepSeconds,
    includeIdleCosts: false,
    enabled: !!cluster.id && !!workload && isRangeSupported,
  });

  const aggregatedByDate = useMemo(
    () => aggregateMetrics(report?.costMetrics as WorkloadCostMetric[]),
    [report]
  );

  const dailyData = useMemo(
    () =>
      buildDailyData(
        aggregatedByDate,
        { from: range.from, to: range.to },
        timeZone
      ),
    [aggregatedByDate, range, timeZone]
  );

  const cumulativeData = useMemo(
    () => buildCumulativeData(dailyData),
    [dailyData]
  );

  const totals = useMemo(() => computeTotals(dailyData), [dailyData]);

  return {
    dailyData,
    cumulativeData,
    totals,
    isLoading,
    isFetching,
    error: error as Error | null,
    refetch,
    timeZone,
    isRangeSupported,
  };
};
